<template>
  <div>
    <v-row no-gutters justify="end">
      <v-col cols="12" sm="10" offset-sm="2" v-if="canCreateComment">
        <v-expand-transition>
          <CommentForm
            class="mt-4"
            :viewContext="viewContext"
            :value="commentText"
            v-show="commentFormOpen"
            @save="createComment"
            @cancel="closeCommentForm()"
          />
        </v-expand-transition>
      </v-col>
    </v-row>
    <CommentList
      ref="commentListComponent"
      v-show="totalLoaded > 0"
      :view-context="viewContext"
      :comments="commentList"
      :delete-comment="deleteComment"
      :update-comment="updateComment"
      @totalLoaded="updateTotalLoaded"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch } from "vue"
import { Comment } from "suus-api"
import CommentForm from "components/Comment/CommentForm.vue"
import { ViewContext } from "concerns/useContextName"
import CommentList from "components/Comment/CommentList.vue"
import useUpdatePostComment from "concerns/useUpdatePostComment"
import useDeleteComment from "concerns/useDeleteComment"
import { postsApi } from "store/api"

export default defineComponent({
  name: "CommentSection",
  components: { CommentList, CommentForm },
  props: {
    initialComments: { type: Array as () => Comment[], default: () => [] },
    commentFormOpen: { type: Boolean, default: false },
    canCreateComment: { type: Boolean, default: false },
    viewContext: { type: Object as PropType<ViewContext> },
    postId: { type: Number, required: true },
  },
  setup(props, context) {
    const commentListComponent = ref<InstanceType<typeof CommentList>>(null)

    const commentText = ref("")

    const commentList = ref(props.initialComments)

    const totalLoaded = ref(0)

    const updateTotalLoaded = (newVal: number) => {
      totalLoaded.value = newVal
    }

    const closeCommentForm = () => {
      context.emit("changeCommentFormVisibility", false)
    }

    const openCommentForm = () => {
      context.emit("changeCommentFormVisibility", true)
    }

    async function createComment(text: string): Promise<void> {
      closeCommentForm()
      try {
        const createdComment = await postsApi
          .createPostComment(props.postId.toString(), {
            text,
          })
          .then((res) => res.data)
        commentListComponent.value?.add(createdComment)
      } catch (e) {
        openCommentForm()
        commentText.value = text
      }
    }

    function updateComment(comment: Comment): Promise<Comment> {
      return new Promise((resolve, reject) => {
        const { updatedComment, error } = useUpdatePostComment(comment)
        watch(updatedComment, (newVal: Comment) => {
          resolve(newVal)
        })
        watch(error, (err) => {
          reject(err)
        })
      })
    }

    function deleteComment(comment: Comment): Promise<number> {
      return new Promise((resolve, reject) => {
        const { isSuccess, error } = useDeleteComment(comment.id.toString())
        watch(isSuccess, (err) => {
          resolve(comment.id)
        })
        watch(error, (err) => {
          reject(err)
        })
      })
    }

    return {
      commentText,
      createComment,
      commentList,
      updateComment,
      deleteComment,
      closeCommentForm,
      totalLoaded,
      updateTotalLoaded,
      commentListComponent,
    }
  },
})
</script>
